import { convertData } from "./BehaviouralBreakdown.data";

// Constants
import { COLORS } from "constants/chartCommons";

// Utils
import { isEmpty } from "lodash";
import { getElementBBox } from "utils";

// Types
import { svgConfig, buttonArgs, TElementConfig, ChartStatus, svgConfigAndExport } from "./BehaviouralBreakdown.types";
import theme from "assets/styles/themes";
import { TCustomiseLabelsOptions } from "../CustomiseLabels/CustomiseLabels.config";

export const dotRadius = 4;
export const rowHeight = 40;
export const buttonHeight = 12;
export const maxValue = 100;
export const gapBetweenBehaviours = 10;
export const chartVerticalSpace = 110;
export const labelChartSeparation = 5;
export const transitionSpeed = 400;
export const legendSpace = 20;
export const hubAverageColor = "#FF0000";
export const hubAverageStrokeDasharray = "2.08 2.08";

const labelDefaultConfig = {
    fill: "transparent",
    "stroke-width": 0,
};
const labelDefaultCSS = {
    whiteSpace: "break-spaces",
    fontFamily: "Lato",
};

export const convertToScale = (barElement: ReturnType<typeof getElementBBox> | undefined, value: number | null) => {
    if (!barElement) return;

    const scaleWidth = barElement.width;
    const xPosition = barElement.x;

    return (scaleWidth * (value || 0)) / maxValue + xPosition;
};

export const createLine = (
    el1: svgConfigAndExport,
    el2: svgConfigAndExport,
    lineConfig: Highcharts.SVGAttributes,
    status: ChartStatus,
    hasSubData = false,
    isSubData = false
) => {
    if (!el1 || !el2) return;

    const extraHeight = hasSubData ? buttonHeight : 0;
    const yPos = isSubData ? 0 : rowHeight + gapBetweenBehaviours + extraHeight;

    const element1 = status.isExporting ? el1.svgExport.element : el1.svg.element;
    const element2 = status.isExporting ? el2.svgExport.element : el2.svg.element;

    // Al usar getElementBBox() estoy tomando la posición de la bolita en pantalla, no la bolita para el export
    const dot1 = getElementBBox(element1);
    const dot2 = getElementBBox(element2);

    return {
        args: [
            ["M", dot1.x - dot1.width / 2, dot1.y - dot1.height / 2],
            ["L", dot2.x - dot2.width / 2, dot2.y + yPos - dot2.height / 2],
        ],
        attr: lineConfig,
    };
};

export const createMasks = (
    dataEntries: [string, ReturnType<typeof convertData>[0]][],
    entry: ReturnType<typeof convertData>[0][],
    status: ChartStatus,
    chart: Highcharts.Chart,
    renderer: Highcharts.SVGRenderer
) => {
    dataEntries.forEach((_, i) => {
        entry[i].svgs.mask.config = {
            ...entry[i].svgs.mask.config,
            args: [0, chart.plotTop, chart.plotWidth, rowHeight + buttonHeight],
        };

        if (status.isFirstRender) {
            entry[i].svgs.mask.svg = renderer.clipRect(...(entry[i].svgs.mask.config.args as number[]));
        }

        if (status.isRedrawing) {
            entry[i].svgs.mask.svg.element?.setAttribute("width", `${chart.plotWidth}`);
        }

        if (status.isExporting) {
            entry[i].svgs.mask.svgExport = renderer
                .clipRect(...(entry[i].svgs.mask.config.args as number[]))
                .css(entry[i].svgs.mask.config.css || {});
        }
    });
};

export const createWrapperGroups = (
    dataEntries: [string, ReturnType<typeof convertData>[0]][],
    entry: ReturnType<typeof convertData>[0][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    previousGroup: Highcharts.SVGElement | undefined
) => {
    dataEntries.forEach((_, i, entries) => {
        let translateY = 0;

        if (previousGroup) {
            const previousGroupTranslateY = previousGroup ? getElementBBox(previousGroup.element).f : 0;
            const subDataHeight =
                entries[i - 1] && entries[i - 1][1].subData?.data?.length ? rowHeight : rowHeight - buttonHeight;

            translateY =
                previousGroupTranslateY +
                subDataHeight +
                gapBetweenBehaviours * (entries[i - 1] && entries[i - 1][1].isLastGraph ? 4 : 2);
        }

        entry[i].svgs.wrapperGroup.config = {
            ...entry[i].svgs.wrapperGroup.config,
            args: [`wrapperGroup-${i}`],
            attr: {
                zIndex: entries.length - i,
            },
            translate: [0, translateY],
        };

        if (status.isFirstRender) {
            entry[i].svgs.wrapperGroup.svg = renderer
                .g(...(entry[i].svgs.wrapperGroup.config.args as string[]))
                .translate(...(entry[i].svgs.wrapperGroup.config.translate as [number, number]))
                .attr(entry[i].svgs.wrapperGroup.config.attr as Highcharts.SVGAttributes)
                .add();
        }

        if (status.isRedrawing && !isEmpty(entry[i].svgs.wrapperGroup.svg)) {
            entry[i].svgs.wrapperGroup.svg.translate(
                entry[i].svgs.wrapperGroup.config.translate?.[0] || 0,
                entry[i].svgs.wrapperGroup.config.translate?.[1] || 0
            );
        }

        if (status.isExporting) {
            entry[i].svgs.wrapperGroup.svgExport = renderer
                .g(...(entry[i].svgs.wrapperGroup.config.args as string[]))
                .css(entry[i].svgs.wrapperGroup.config.css || {})
                .translate(...(entry[i].svgs.wrapperGroup.config.translate as [number, number]))
                .attr(entry[i].svgs.wrapperGroup.config.attr as Highcharts.SVGAttributes)
                .add();
        }

        entry[i].svgs.group.config = {
            args: [`group-${i}`],
        };

        if (status.isFirstRender) {
            entry[i].svgs.group.svg = renderer
                .g(...(entry[i].svgs.group.config.args as string[]))
                .add(entry[i].svgs.wrapperGroup.svg);
        }

        if (status.isExporting) {
            entry[i].svgs.group.svgExport = renderer
                .g(...(entry[i].svgs.group.config.args as string[]))
                .add(entry[i].svgs.wrapperGroup.svgExport);
        }

        previousGroup = status.isExporting ? entry[i].svgs.wrapperGroup.svgExport : entry[i].svgs.wrapperGroup.svg;
    });
};

export const createSubBehaviourBackground = (
    entry: ReturnType<typeof convertData>[0],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number
) => {
    entry.svgs.subBehaviourBar.config = {
        ...entry.svgs.subBehaviourBar.config,
        args: [
            chartVerticalSpace, // x
            chart.plotTop, // y
            chartMaxWidth,
            rowHeight * (entry.subData?.data?.length || 1),
        ],
        attr: { fill: entry.styles.bgColor },
    };

    if (status.isFirstRender) {
        entry.svgs.subBehaviourBar.svg = renderer
            .rect(...(entry.svgs.subBehaviourBar.config.args as number[]))
            .attr(entry.svgs.subBehaviourBar.config.attr)
            .add(entry.svgs.group.svg);
    }

    if (status.isRedrawing) {
        entry.svgs.subBehaviourBar.svg.element?.setAttribute("width", `${chartMaxWidth}`);
    }

    if (status.isExporting) {
        entry.svgs.subBehaviourBar.svgExport = renderer
            .rect(...(entry.svgs.subBehaviourBar.config.args as number[]))
            .attr(entry.svgs.subBehaviourBar.config.attr)
            .add(entry.svgs.group.svgExport);
    }
};

export const createBehaviourBar = (
    entry: ReturnType<typeof convertData>[0],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number
) => {
    entry.svgs.bar.config = {
        ...entry.svgs.bar.config,
        args: [
            chartVerticalSpace, //x
            chart.plotTop, //y
            chartMaxWidth,
            40,
        ],
        attr: { fill: entry.styles.bgColor },
    };

    if (status.isFirstRender) {
        entry.svgs.bar.svg = renderer
            .rect(...(entry.svgs.bar.config.args as number[]))
            .attr(entry.svgs.bar.config.attr)
            .add(entry.svgs.group.svg);
    }

    if (status.isRedrawing) {
        entry.svgs.bar.svg.element?.setAttribute("width", `${chartMaxWidth}`);
    }

    if (status.isExporting) {
        entry.svgs.bar.svgExport = renderer
            .rect(...(entry.svgs.bar.config.args as number[]))
            .attr(entry.svgs.bar.config.attr)
            .add(entry.svgs.group.svgExport);
    }
};

export const createExpanderButton = (
    entry: ReturnType<typeof convertData>[0],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number,
    showDefinitions: boolean,
    onClickBtn: (i: number, chart: Highcharts.Chart, transitionSpeed: number) => void
) => {
    const pointerEvents = showDefinitions ? "none" : "auto";
    const btnState = {
        width: chartMaxWidth,
        height: buttonHeight,
        padding: 0,
        fill: entry.styles.buttonColor,
        "stroke-width": 0,
        zIndex: 1,
    };

    entry.svgs.button.config = {
        args: [
            "",
            chartVerticalSpace, // x
            0, // y
            onClickBtn,
            btnState,
            btnState,
            btnState,
            btnState,
        ],
        css: entry.svgs.button.config.css
            ? entry.svgs.button.config.css
            : {
                  transform: `translate(${chartVerticalSpace}px, ${rowHeight + 2}px)`,
                  pointerEvents,
              },
    };

    if (status.isFirstRender) {
        entry.svgs.button.svg = renderer
            .button(...(entry.svgs.button.config.args as buttonArgs))
            .css((entry.svgs.button.config.css || {}) as Highcharts.CSSObject)
            .add(entry.svgs.group.svg);
    }

    if (status.isRedrawing) {
        const button = entry.svgs.button.svg;
        const buttonBg = button.element.querySelector("rect");

        button.css({ pointerEvents });

        if (buttonBg) {
            buttonBg.setAttribute("width", `${chartMaxWidth}`);
            button.on("mouseleave", function () {
                buttonBg.setAttribute("width", `${chartMaxWidth}`);
            });
        }
    }

    if (status.isExporting) {
        renderer
            .button(...(entry.svgs.button.config.args as buttonArgs))
            .css((entry.svgs.button.config.css || {}) as Highcharts.CSSObject)
            .add(entry.svgs.group.svgExport);
    }

    Array.from(Array(2)).forEach((_, idx) => {
        if (isEmpty(entry.svgs.buttonChevrons?.[idx]?.config)) {
            entry.svgs.buttonChevrons[idx] = {
                config: {} as TElementConfig,
                svg: {} as Highcharts.SVGElement,
                svgExport: {} as Highcharts.SVGElement,
            };
        }

        const translateX = idx > 0 ? chartMaxWidth + chartVerticalSpace - 30 : 15 + chartVerticalSpace;
        const translateY = isEmpty(entry.svgs.buttonChevrons[idx].config.css)
            ? rowHeight + 2
            : entry.svgs.buttonChevrons[idx].config.css["transform"].split(" ")[1].replace("px)", "");

        const rotate = entry.svgs.buttonChevrons[idx].config?.css?.["transform"]?.split(" ")?.[2];

        entry.svgs.buttonChevrons[idx].config = {
            ...entry.svgs.buttonChevrons[idx].config,
            args: ["M", 1.0293, 1.70654, "L", 7.0293, 7.70654, "L", 13.0293, 1.70654],
            attr: {
                stroke: "#FDFEFF",
                "stroke-width": 1.5,
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                zIndex: 2,
            },
            css: {
                transform: `translate(${translateX}px, ${translateY}px) ${rotate || ""}`,
            },
        };

        if (status.isFirstRender) {
            entry.svgs.buttonChevrons[idx].svg = renderer
                .path(entry.svgs.buttonChevrons[idx].config.args as Highcharts.SVGPathArray)
                .attr(entry.svgs.buttonChevrons[idx].config.attr)
                .css(entry.svgs.buttonChevrons[idx].config.css as Highcharts.CSSObject)
                .add(entry.svgs.group.svg);
        }

        if (status.isRedrawing) {
            entry.svgs.buttonChevrons[idx]?.svg?.css(entry.svgs.buttonChevrons[idx].config.css as Highcharts.CSSObject);
        }

        if (status.isExporting) {
            entry.svgs.buttonChevrons[idx].svgExport = renderer
                .path(entry.svgs.buttonChevrons[idx].config.args as Highcharts.SVGPathArray)
                .attr(entry.svgs.buttonChevrons[idx].config.attr)
                .css(entry.svgs.buttonChevrons[idx].config.css as Highcharts.CSSObject)
                .add(entry.svgs.group.svgExport);
        }
    });
};

export const createPlotLines = (
    entry: ReturnType<typeof convertData>[0],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number,
    group: Highcharts.SVGElement
) => {
    entry.svgs.plotLines.forEach((el, i) => {
        const config = {
            args: [
                (chartMaxWidth / 10) * ++i + chartVerticalSpace,
                0,
                1,
                (entry.subData?.data.length || 1) * rowHeight,
            ],
            attr: { fill: "white" },
        };

        el.config = config;

        if (status.isFirstRender) {
            el.svg = renderer
                .rect(...(config.args as number[]))
                .attr(config.attr)
                .add(group);
        }

        if (status.isRedrawing) {
            el.svg?.attr({
                x: config.args[0],
                height: config.args[3],
            });
        }

        if (status.isExporting) {
            el.svgExport = renderer
                .rect(...(config.args as number[]))
                .attr(config.attr)
                .add(group);
        }
    });

    entry.subData?.data &&
        entry.subData.data.forEach((_, i) => {
            const config = {
                args: [
                    chartVerticalSpace, // x
                    chart.plotTop + (i + 1) * rowHeight, // y
                    chartMaxWidth,
                    1,
                ],
                attr: { fill: "white" },
            };

            entry.svgs.subData.plotLines[i].config = config;

            if (status.isFirstRender) {
                entry.svgs.subData.plotLines[i].svg = renderer
                    .rect(...(config.args as number[]))
                    .attr(config.attr)
                    .add(group);
            }

            if (status.isRedrawing) {
                entry.svgs.subData.plotLines[i].svg?.attr({
                    x: config.args[0],
                    y: config.args[1],
                    width: config.args[2],
                });
            }

            if (status.isExporting) {
                entry.svgs.subData.plotLines[i].svgExport = renderer
                    .rect(...(config.args as number[]))
                    .attr(config.attr)
                    .add(group);
            }
        });
};

export const createLabels = (
    entry: ReturnType<typeof convertData>[0],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number,
    shouldShowDefinitions: boolean,
    barElement: Record<string, number>,
    onClickLabel: (label: string, parentLabel: string) => void
) => {
    entry.labels.forEach((label, idx) => {
        const labelConfig = {
            args: [
                label,
                idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : barElement.x - 20,
                0,
                () => onClickLabel(label, entry.labels[0]),
                labelDefaultConfig,
                labelDefaultConfig,
                labelDefaultConfig,
            ],
            css: entry.svgs.labels?.[idx]?.config.css
                ? entry.svgs.labels[idx].config.css
                : {
                      ...labelDefaultCSS,
                      opacity: 1,
                      fontSize: "12px",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      width: 93,
                      ...(idx === 0 ? { textAnchor: "end" } : {}),
                  },
        };

        // View Definitions - Change labels styles to look like links
        if (shouldShowDefinitions) {
            entry.svgs.labels[idx]?.svg.addClass("chart-carousel-axis-label--active");
        } else {
            entry.svgs.labels[idx]?.svg.removeClass("chart-carousel-axis-label--active");
        }

        if (status.isFirstRender) {
            const labelSvg = renderer
                // @ts-ignore
                .button(...labelConfig.args)
                .css(labelConfig.css as Highcharts.CSSObject)
                .addClass("chart-carousel-axis-label")
                .add(entry.svgs.group.svg);

            labelSvg.attr({
                y: (barElement.height + 0) / 2 - labelSvg.getBBox().height / 2,
            });

            entry.svgs.labels[idx] = { config: labelConfig, svg: labelSvg };
        }

        if (status.isRedrawing) {
            entry.svgs.labels[idx].svg?.attr({
                x: idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : barElement.x - 20,
            });
        }

        if (status.isExporting) {
            renderer
                // @ts-ignore
                .button(...labelConfig.args)
                .css(labelConfig.css as Highcharts.CSSObject)
                .add(entry.svgs.group.svgExport)
                .attr({
                    x: idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : barElement.x - 20,
                });
        }

        // ROTATED LABELS
        if (entry.subData.data.length) {
            const bars =
                barElement.height * entry.subData.data.length +
                //@ts-ignore
                (entry.svgs.button.svg.element.getBBox().height || 0);

            const rotatedLabelConfig = {
                args: [label, 0, 0],
                css: entry.svgs.rotatedLabels[idx]?.config.css
                    ? entry.svgs.rotatedLabels[idx].config.css
                    : {
                          ...labelDefaultCSS,
                          opacity: 0,
                          color: entry.styles.rotatedLabelsColor,
                          fontSize: "15px",
                          textTransform: "uppercase",
                      },
            };

            if (status.isFirstRender) {
                const rotatedLabelSvg = renderer
                    // @ts-ignore
                    .label(...rotatedLabelConfig.args)
                    .css(rotatedLabelConfig.css as Highcharts.CSSObject)
                    .add(entry.svgs.group.svg);

                const translate = {
                    x: idx > 0 ? chart.plotWidth - rotatedLabelSvg.getBBox().height - 10 : 0,
                    y: bars / 2 + rotatedLabelSvg.getBBox().width / 2,
                };

                rotatedLabelSvg.css({
                    transform: `translate(${translate.x}px, ${translate.y}px) rotate(-90deg)`,
                });

                entry.svgs.rotatedLabels[idx] = { config: rotatedLabelConfig, svg: rotatedLabelSvg };
            }

            if (status.isRedrawing) {
                const svgBBox = entry.svgs.rotatedLabels[idx].svg?.getBBox();
                const translate = {
                    x: idx > 0 ? chart.plotWidth - svgBBox.height : 0,
                    y: bars / 2 + svgBBox.width / 2,
                };

                entry.svgs.rotatedLabels[idx].svg?.css({
                    transform: `translate(${translate.x}px, ${translate.y}px) rotate(-90deg)`,
                });
            }

            if (status.isExporting) {
                const svgBBox = entry.svgs.rotatedLabels[idx].svg?.getBBox();
                const translate = {
                    x: idx > 0 ? chart.plotWidth - svgBBox.height : 0,
                    y: bars / 2 + svgBBox.width / 2,
                };

                renderer
                    // @ts-ignore
                    .label(...rotatedLabelConfig.args)
                    .css({
                        ...rotatedLabelConfig.css,
                        transform: `translate(${translate.x}px, ${translate.y}px) rotate(-90deg)`,
                    } as Highcharts.CSSObject)
                    .add(entry.svgs.group.svgExport);
            }
        }
    });
};

export const createSubLabels = (
    entry: ReturnType<typeof convertData>[0],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chartMaxWidth: number,
    shouldShowDefinitions: boolean,
    onClickLabel: (label: string, parentLabel: string) => void
) => {
    entry.subData.labels?.length &&
        entry.subData.labels.forEach((label, labelIdx) => {
            entry.svgs.subData.labels.push([]);
            label.forEach((text, idx) => {
                const xPosition =
                    idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : 82 + labelChartSeparation;

                const config = {
                    args: [
                        text,
                        xPosition,
                        0,
                        () => onClickLabel(text, entry.labels[0]),
                        labelDefaultConfig,
                        labelDefaultConfig,
                        labelDefaultConfig,
                    ],
                    attr: {},
                    css: entry.svgs.subData.labels?.[labelIdx]?.[idx]?.config.css
                        ? entry.svgs.subData.labels[labelIdx][idx].config.css
                        : {
                              ...labelDefaultCSS,
                              opacity: 0,
                              fontSize: "10px",
                              width: 50,
                              color: theme.palette.text.primary,
                              ...(idx === 0 ? { textAnchor: "end" } : {}),
                          },
                };

                // View Definitions - Change labels styles to look like links
                if (shouldShowDefinitions) {
                    entry.svgs.subData.labels[labelIdx]?.[idx]?.svg.addClass("chart-carousel-axis-label--active");
                } else {
                    entry.svgs.subData.labels[labelIdx]?.[idx]?.svg.removeClass("chart-carousel-axis-label--active");
                }

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .button(...config.args)
                        .css(config.css as Highcharts.CSSObject)
                        .addClass("chart-carousel-axis-label")
                        .add(entry.svgs.group.svg);

                    entry.svgs.subData.labels[labelIdx].push({ config, svg });
                }

                if (status.isRedrawing) {
                    const svg = entry.svgs.subData.labels[labelIdx][idx]?.svg;
                    const coors = {
                        x: xPosition,
                        y: rowHeight * (labelIdx + 1) - rowHeight / 2 - svg.element.getBoundingClientRect().height / 2,
                    };
                    svg.attr(coors);
                }

                if (status.isExporting) {
                    const svg = entry.svgs.subData.labels[labelIdx][idx]?.svg;
                    const coors = {
                        x: xPosition,
                        y: rowHeight * (labelIdx + 1) - rowHeight / 2 - svg.element.getBoundingClientRect().height / 2,
                    };
                    renderer
                        // @ts-ignore
                        .button(...config.args)
                        .css(config.css as Highcharts.CSSObject)
                        .addClass("chart-carousel-axis-label")
                        .add(entry.svgs.group.svgExport)
                        .attr(coors);
                }
            });
        });
};

export const createSummaryDot = (
    dataEntries: [string, ReturnType<typeof convertData>[0]][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    barElement: ReturnType<typeof getElementBBox> | undefined
) => {
    dataEntries.forEach((el) => {
        el[1].data.forEach((element, idx) => {
            const isHubAverage = el[1].data[idx].label === "Hub Average";
            const fillColor = isHubAverage ? hubAverageColor : COLORS[idx];
            const isEnabled = element.enabled;
            const opacity = el[1].isVisible[idx] ? 1 : 0;

            const config = {
                args: [convertToScale(barElement, element.value), 20, dotRadius],
                attr: {
                    id: `main-${idx}--${element.label}-${element.value}`,
                    zIndex: 2,
                },
                css: el[1].svgs.dots[idx]?.config.css
                    ? el[1].svgs.dots[idx]?.config.css
                    : { fill: !isEnabled ? "transparent" : fillColor, opacity: !isEnabled ? 0 : opacity },
            };

            if (status.isFirstRender) {
                const svg = renderer
                    .circle(...(config.args as unknown as Highcharts.SVGAttributes[]))
                    .attr(config.attr)
                    // @ts-ignore
                    .css(config.css as Highcharts.CSSObject)
                    .add(el[1].svgs.group.svg);

                el[1].svgs.dots[idx] = { ...el[1].svgs.dots[idx], config, svg };
            }

            if (status.isRedrawing) {
                el[1].svgs.dots[idx].svg?.attr({
                    x: convertToScale(barElement, element.value),
                });
            }

            if (status.isExporting) {
                el[1].svgs.dots[idx].svgExport = renderer
                    .circle(...(config.args as unknown as Highcharts.SVGAttributes[]))
                    .attr({ ...config.attr, x: convertToScale(barElement, element.value) })
                    // @ts-ignore
                    .css(config.css as Highcharts.CSSObject)
                    .add(el[1].svgs.group.svgExport);
            }
        });
    });
};

export const createSummaryDotLabel = (
    dataEntries: [string, ReturnType<typeof convertData>[0]][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    customiseLabelsOptions: TCustomiseLabelsOptions
) => {
    dataEntries.forEach((el, i) => {
        if (i === 0 || i === 4) {
            el[1].data.forEach((element, idx) => {
                const isEnabled = element.enabled;
                const isVisible = el[1].isVisible[idx] ? 1 : 0;
                const showNames = customiseLabelsOptions["show-names"];
                const isExpanded = el[1].expanded;

                const labelText = isVisible && !isExpanded && showNames && isEnabled ? element.label : "";
                const dot = status.isExporting ? el[1].svgs.dots[idx].svgExport : el[1].svgs.dots[idx].svg;

                const config = el[1].svgs.dotsLabels?.[idx]?.config
                    ? el[1].svgs.dotsLabels[idx].config
                    : {
                          args: [labelText, getElementBBox(dot.element).x, getElementBBox(dot.element).y - 20],
                          css: {
                              fontFamily: "Lato",
                              fontSize: "12px",
                              textAnchor: (el[1].data[idx].value || 0) >= 75 ? "end" : "start",
                              color: "#454852",
                          },
                          attr: {
                              zIndex: 2,
                          },
                      };

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .label(...(config.args as Highcharts.SVGAttributes[]))
                        .css(config.css as Highcharts.CSSObject)
                        .attr(config.attr)
                        // @ts-ignore
                        .add(el[1].svgs.group.svg);

                    el[1].svgs.dotsLabels[idx] = { config, svg };
                }

                if (status.isRedrawing) {
                    el[1].svgs.dotsLabels[idx].svg
                        ?.attr({
                            text: labelText,
                            x: getElementBBox(dot.element).x,
                        })
                        .css(config.css as Highcharts.CSSObject);
                }

                if (status.isExporting) {
                    const labelConfig = [labelText, getElementBBox(dot.element).x, getElementBBox(dot.element).y - 20];
                    renderer
                        // @ts-ignore
                        .label(...labelConfig)
                        .css(config.css as Highcharts.CSSObject)
                        // @ts-ignore
                        .add(el[1].svgs.group.svgExport);
                }
            });
        }
    });
};

export const createDotLineConnector = (
    dataEntries: [string, ReturnType<typeof convertData>[0]][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer
) => {
    dataEntries.forEach((entry, entryIdx) => {
        if (!entry[1].isLastGraph) {
            const data = dataEntries[entryIdx][1];

            entry[1].svgs.dots.forEach((dot, dotIdx) => {
                const isEnabled = entry[1].data[dotIdx].enabled;
                const isHubAverage = entry[1].data[dotIdx].label === "Hub Average";
                const strokeWidth = entry[1].svgs?.lines?.[dotIdx]?.config?.css
                    ? entry[1].svgs.lines[dotIdx].config.css?.["stroke-width"]
                    : 1;

                const config = createLine(
                    dot,
                    dataEntries[entryIdx + 1][1].svgs.dots[dotIdx],
                    {
                        "stroke-width": !isEnabled ? 0 : strokeWidth,
                        stroke: isHubAverage ? hubAverageColor : COLORS[dotIdx],
                        ...(isHubAverage ? { "stroke-dasharray": hubAverageStrokeDasharray } : {}),
                    },
                    status,
                    Boolean(entry[1].subData.data.length)
                );

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .path(config.args)
                        // @ts-ignore
                        .attr(config.attr)
                        // @ts-ignore
                        .css(config.css || {})
                        .add(data.svgs.wrapperGroup.svg);

                    entry[1].svgs.lines.push({ svg, ...{ config: config || {} } });
                }

                if (status.isRedrawing) {
                    if (config) {
                        entry[1].svgs.lines[dotIdx].svg?.attr({
                            d: config.args as any,
                        });
                    }
                }

                if (status.isExporting) {
                    renderer
                        // @ts-ignore
                        .path(config.args)
                        // @ts-ignore
                        .attr(config.attr)
                        // @ts-ignore
                        .css(config.css || {})
                        .add(data.svgs.wrapperGroup.svgExport);
                }
            });
        }
    });
};

export const createSubBehaviourDots = (
    dataEntries: [string, ReturnType<typeof convertData>[0]][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    barElement: ReturnType<typeof getElementBBox> | undefined,
    customiseLabelsOptions: TCustomiseLabelsOptions
) => {
    dataEntries.forEach((el, i) => {
        el[1].subData?.data?.forEach((arrayOfDots, arrayOfDotsIdx) => {
            if (status.isFirstRender) {
                el[1].svgs.subData.dots.push([]);
            }

            arrayOfDots.forEach((dot, idx) => {
                const isVisible = el[1].isVisible[idx] ? 1 : 0;
                const isEnabled = el[1].data[idx].enabled;
                const isExpanded = el[1].expanded;

                const isHubAverage = el[1].data[idx].label === "Hub Average";
                const fillColor = isHubAverage ? hubAverageColor : COLORS[idx];
                const fillOpacity = isVisible && isExpanded ? 1 : 0;

                const config = {
                    args: [
                        convertToScale(barElement, dot || 0),
                        (barElement?.height || 1) / 2 + arrayOfDotsIdx * 40,
                        dotRadius,
                    ],
                    attr: {
                        id: `sub-${idx}--${dot}`,
                        zIndex: 2,
                    },
                    css: {
                        ...el[1].svgs.dots[idx]?.config.css,
                        fill: !isEnabled ? "transparent" : fillColor,
                        opacity: !isEnabled ? 0 : fillOpacity,
                    },
                };

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .circle(...config.args)
                        .attr(config.attr)
                        // @ts-ignore
                        .css(config.css || {})
                        .add(el[1].svgs.group.svg);

                    el[1].svgs.subData.dots[arrayOfDotsIdx].push({
                        config,
                        svg,
                        svgExport: {} as Highcharts.SVGElement,
                    });
                }

                if (status.isRedrawing) {
                    el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svg
                        ?.attr({
                            ...(config?.attr ? (config.attr as Highcharts.SVGAttributes) : {}),
                            x: convertToScale(barElement, dot || 0),
                        })
                        .css(config.css || {});
                }

                if (status.isExporting) {
                    el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svgExport = renderer
                        // @ts-ignore
                        .circle(...config.args)
                        .attr(config.attr)
                        .attr({ x: convertToScale(barElement, dot || 0) })
                        // @ts-ignore
                        .css(config.css || {})
                        .add(el[1].svgs.group.svgExport);
                }

                // SUB-BEHAVIOUR DOT LABELS
                if (arrayOfDotsIdx === 0) {
                    const isExpanded = el[1].expanded;
                    const isVisible = el[1].isVisible[idx] ? 1 : 0;
                    const showNames = customiseLabelsOptions["show-names"];
                    const labelText = isVisible && isExpanded && showNames ? el[1].data[idx].label : "";

                    const firstDot = status.isExporting
                        ? el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svgExport
                        : el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svg;

                    const config = el[1].svgs.subData.dotsLabels?.[idx]?.config
                        ? el[1].svgs.subData.dotsLabels[idx].config
                        : {
                              args: [labelText, 0, 0],
                              css: {
                                  fontFamily: "Lato",
                                  fontSize: "12px",
                                  textAnchor: (el[1].data[idx].value || 0) >= 75 ? "end" : "start",
                              },
                              attr: {
                                  zIndex: 2,
                              },
                          };

                    if (status.isFirstRender) {
                        const svg = renderer
                            // @ts-ignore
                            .label(...config.args)
                            .css(config.css as Highcharts.CSSObject)
                            .attr(config.attr)
                            .add(el[1].svgs.group.svg);

                        svg.attr({
                            x: getElementBBox(firstDot.element).x,
                            y: getElementBBox(firstDot.element).y - 20,
                        });

                        el[1].svgs.subData.dotsLabels[idx] = { config, svg };
                    }

                    if (status.isRedrawing) {
                        el[1].svgs.subData.dotsLabels[idx].svg?.attr({
                            text: labelText,
                            x: getElementBBox(firstDot.element).x,
                        });
                    }

                    if (status.isExporting) {
                        const labelConfig = [
                            labelText,
                            getElementBBox(firstDot.element).x,
                            getElementBBox(firstDot.element).y - 20,
                        ];
                        renderer
                            // @ts-ignore
                            .label(...labelConfig)
                            .css(config.css as Highcharts.CSSObject)
                            .attr(config.attr)
                            .add(el[1].svgs.group.svgExport);
                    }
                }
            });
        });
    });
};

export const createSubDotLineConnector = (
    dataEntries: [string, ReturnType<typeof convertData>[0]][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer
) => {
    dataEntries.forEach((entry, entryIdx) => {
        entry[1].svgs.subData?.dots.forEach((arrayOfDots, arrayOfDotsIdx) => {
            if (arrayOfDotsIdx + 1 < entry[1].svgs.subData.dots.length) {
                const data = dataEntries[entryIdx][1];

                if (status.isFirstRender) {
                    data.svgs.subData.lines.push([]);
                }

                arrayOfDots.forEach((dot, dotIdx) => {
                    const isVisible = data.isVisible[dotIdx];
                    const isEnabled = entry[1].data[dotIdx].enabled;
                    const isExpanded = data.expanded;

                    const isHubAverage = entry[1].data[dotIdx].label === "Hub Average";
                    const strokeWidth = isVisible && isExpanded ? 1 : 0;

                    const config = createLine(
                        dot,
                        entry[1].svgs.subData.dots[arrayOfDotsIdx + 1][dotIdx],
                        {
                            "stroke-width": !isEnabled ? 0 : strokeWidth,
                            stroke: isHubAverage ? hubAverageColor : COLORS[dotIdx],
                            ...(isHubAverage ? { "stroke-dasharray": hubAverageStrokeDasharray } : {}),
                        },
                        status,
                        Boolean(entry[1].subData.data.length),
                        true
                    );

                    if (!config) return;

                    if (status.isFirstRender) {
                        const svg = renderer
                            // @ts-ignore
                            .path(config.args)
                            // @ts-ignore
                            .attr(config.attr)
                            // @ts-ignore
                            .css(config.css || {})
                            .add(data.svgs.group.svg);

                        data.svgs.subData.lines[arrayOfDotsIdx].push({ svg, config });
                    }

                    if (status.isRedrawing) {
                        data.svgs.subData.lines[arrayOfDotsIdx][dotIdx].svg?.attr({
                            ...config.attr,
                            d: config.args as any,
                        });
                    }

                    if (status.isExporting) {
                        renderer
                            // @ts-ignore
                            .path(config.args)
                            // @ts-ignore
                            .attr(config.attr)
                            // @ts-ignore
                            .css(config.css || {})
                            .add(data.svgs.group.svgExport);
                    }
                });
            }
        });
    });
};
